import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Radio, ExternalLink, ChevronDown } from "lucide-react";
import ScoreNotifier from "@/components/ScoreNotifier";

/**
 * Live Score Bar — Titans game state (pre / live / same-day final) with
 * quarter-by-quarter line score and an expandable "around the league"
 * scoreboard of every other NFL game. Data: ESPN via /api/live-scoreboard.
 * Polls every 25s in-play, 60s pregame on gameday, 5min otherwise.
 */
const QLABELS = ["1", "2", "3", "4", "OT"];

const isSameDay = (iso) => {
  if (!iso) return false;
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
};

const LineScore = ({ ev }) => {
  const cols = Math.max(ev.home.linescores?.length || 0, ev.away.linescores?.length || 0);
  if (!cols) return null;
  return (
    <table className="text-[11px] font-cond leading-none" data-testid="titans-linescore">
      <thead>
        <tr className="text-white/60">
          <th className="pr-2 text-left font-normal" />
          {Array.from({ length: cols }).map((_, i) => (
            <th key={i} className="px-1.5 font-normal">{QLABELS[i] || `OT${i - 3}`}</th>
          ))}
          <th className="pl-2 font-bold">T</th>
        </tr>
      </thead>
      <tbody>
        {["away", "home"].map((side) => (
          <tr key={side}>
            <td className="pr-2 uppercase tracking-wider">{ev[side].abbr}</td>
            {Array.from({ length: cols }).map((_, i) => (
              <td key={i} className="px-1.5 text-center tabular-nums">{ev[side].linescores?.[i] ?? "-"}</td>
            ))}
            <td className="pl-2 text-center font-bold tabular-nums">{ev[side].score}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const MiniGame = ({ g }) => {
  const live = g.state === "in";
  return (
    <a
      href={`https://www.espn.com/nfl/game/_/gameId/${g.id}`}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`league-game-${g.id}`}
      className="shrink-0 flex items-center gap-2 bg-black/20 hover:bg-black/30 rounded px-2.5 py-1.5 transition-colors"
    >
      {[g.away, g.home].map((t, i) => (
        <span key={i} className="flex items-center gap-1">
          {t.logo && <img src={t.logo} alt="" width="16" height="16" loading="lazy" className="w-4 h-4 object-contain" />}
          <span className="font-cond uppercase text-[11px] tracking-wider">{t.abbr}</span>
          <span className="font-display text-sm leading-none tabular-nums">{t.score}</span>
          {i === 0 && <span className="opacity-50 text-[10px] mx-0.5">@</span>}
        </span>
      ))}
      <span className={`ml-1 font-cond uppercase tracking-wider text-[9px] ${live ? "text-yellow-300" : "opacity-70"}`}>
        {live ? `Q${g.period} ${g.clock}` : g.state === "post" ? "Final" : (g.state_detail || "").replace(" EDT", "").replace(" EST", "")}
      </span>
    </a>
  );
};

export default function LiveScoreBar() {
  const [board, setBoard] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      let delay = 5 * 60_000;
      try {
        const { data } = await api.get("/live-scoreboard");
        if (cancelled) return;
        setBoard(data || null);
        const te = data?.titans_event;
        const anyLive = te?.state === "in" || (data?.events || []).some((e) => e.state === "in");
        if (anyLive) delay = 25_000;
        else if (te && te.state === "pre" && isSameDay(te.date)) delay = 60_000;
      } catch { /* silent */ }
      timer = setTimeout(tick, delay);
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  const ev = board?.titans_event;
  if (!ev || hidden) return null;
  const isLive = ev.state === "in";
  const isFinal = ev.state === "post";
  // Pre + live always; finals only on the same calendar day (no stale bars).
  if (isFinal && !isSameDay(ev.date)) return null;
  if (!isLive && !isFinal && ev.state !== "pre") return null;

  const others = (board?.events || []).filter((g) => g.id !== ev.id);

  return (
    <div
      data-testid="live-score-bar"
      className={`w-full ${isLive ? "bg-titans-red" : "bg-titans-navy border-b border-titans-blue/40"} text-white`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-2 flex items-center gap-3 text-sm">
        {isLive ? (
          <span className="inline-flex items-center gap-1.5 bg-black/25 px-2 py-0.5 rounded-full font-cond uppercase tracking-wider text-[10px]">
            <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
            <Radio size={11} /> Live
          </span>
        ) : (
          <span className="bg-white/15 px-2 py-0.5 rounded-full font-cond uppercase tracking-wider text-[10px]">
            {isFinal ? "Final" : "Next Up"}
          </span>
        )}
        <div className="flex items-center gap-2 font-cond uppercase tracking-wider">
          <span>{ev.away.abbr}</span>
          <span className="font-display text-xl leading-none tabular-nums">{ev.away.score ?? "—"}</span>
          <span className="opacity-60">·</span>
          <span>{ev.home.abbr}</span>
          <span className="font-display text-xl leading-none tabular-nums">{ev.home.score ?? "—"}</span>
        </div>
        <span className="opacity-90 font-cond uppercase tracking-wider text-xs whitespace-nowrap">
          {isLive ? `Q${ev.period || ""} · ${ev.clock || ""}` : ev.state_detail || ""}
          {ev.broadcast && !isFinal ? ` · ${ev.broadcast}` : ""}
        </span>
        {ev.down_distance && isLive && (
          <span className="hidden md:inline opacity-80 text-xs" data-testid="live-down-distance">{ev.down_distance}</span>
        )}
        {ev.last_play && isLive && (
          <span className="hidden lg:inline opacity-80 text-xs truncate max-w-[380px]" data-testid="live-last-play">{ev.last_play}</span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Link
            to="/gameday"
            data-testid="live-score-bar-hub-link"
            className="inline-flex items-center gap-1 font-cond uppercase tracking-wider text-[10px] opacity-90 hover:opacity-100"
          >
            Game Hub
          </Link>
          <ScoreNotifier variant="bar" />
          <button
            onClick={() => setExpanded((x) => !x)}
            data-testid="live-score-expand"
            className="inline-flex items-center gap-1 opacity-90 hover:opacity-100 font-cond uppercase tracking-wider text-[10px]"
            aria-expanded={expanded}
          >
            Scores <ChevronDown size={12} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
          <a
            href={`https://www.espn.com/nfl/game/_/gameId/${ev.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex opacity-90 hover:opacity-100 items-center gap-1 font-cond uppercase tracking-wider text-[10px]"
          >
            Gamecast <ExternalLink size={10} />
          </a>
          <button
            onClick={() => setHidden(true)}
            aria-label="Dismiss"
            data-testid="live-score-bar-close"
            className="opacity-70 hover:opacity-100 px-1"
          >
            ×
          </button>
        </div>
      </div>

      {expanded && (
        <div className={`${isLive ? "bg-black/20" : "bg-black/25"} border-t border-white/10`} data-testid="league-scoreboard">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-2.5 flex items-start gap-4 flex-wrap md:flex-nowrap">
            {(isLive || isFinal) && (ev.home.linescores?.length || ev.away.linescores?.length) ? (
              <div className="shrink-0 pr-4 border-r border-white/10">
                <LineScore ev={ev} />
              </div>
            ) : null}
            <div className="flex-1 min-w-0 overflow-x-auto scrollbar-none">
              <div className="flex items-center gap-2 w-max">
                {others.length ? (
                  others.map((g) => <MiniGame key={g.id} g={g} />)
                ) : (
                  <span className="opacity-70 font-cond uppercase tracking-wider text-[11px]">No other games on the slate</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
